import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { globalObject } from '@skbkontur/global-object';
import { forwardRefAndName } from "../../../../lib/forwardRefAndName";
import { formatBytes } from "../../../../lib/utils";
import { TextWidthHelper } from "../../../TextWidthHelper/TextWidthHelper";
import { truncate } from "../../../../lib/stringUtils";
import { FileUploaderControlContext } from "../../FileUploaderControlContext";
import { cx } from "../../../../lib/theming/Emotion";
import { ThemeContext } from "../../../../lib/theming/ThemeContext";
import { keyListener } from "../../../../lib/events/keyListener";
import { isKeyEnter } from "../../../../lib/events/keyboard/identifiers";
import { Hint } from "../../../../components/Hint";
import { Tooltip } from "../../../../components/Tooltip";
import { getDOMRect } from "../../../../lib/dom/getDOMRect";
import { useFileUploaderSize } from "../../hooks/useFileUploaderSize";
import { jsStyles } from "../FileUploaderFile.styles";
import { FileUploaderFileStatusIcon } from "../FileUploaderFileStatusIcon";

var getTruncatedName = function getTruncatedName(fileNameWidth, fileNameElementWidth, name) {
  if (!fileNameWidth && !fileNameElementWidth) {
    return null;
  }

  if (fileNameWidth <= fileNameElementWidth) {
    return name;
  }

  var charWidth = Math.ceil(fileNameWidth / name.length);
  var maxCharsCountInSpan = Math.ceil(fileNameElementWidth / charWidth);
  return truncate(name, maxCharsCountInSpan);
};

var calcTruncatedName = function calcTruncatedName(textHelperRef, fileNameElementRef, name) {
  var _textHelperRef$curren;

  var fileNameWidth = ((_textHelperRef$curren = textHelperRef.current) == null ? void 0 : _textHelperRef$curren.getTextWidth()) || 0;
  var fileNameElementWidth = getDOMRect(fileNameElementRef.current).width;
  return getTruncatedName(fileNameWidth, fileNameElementWidth, name);
};

var MIN_CHARS_LENGTH = 3;
export var FileUploaderFileDataTids = {
  file: 'FileUploader__file',
  fileTooltip: 'FileUploader__fileTooltip',
  fileName: 'FileUploader__fileName',
  fileSize: 'FileUploader__fileSize',
  fileIcon: 'FileUploader__fileIcon'
};
export var FileUploaderFile = forwardRefAndName('FileUploaderFile', function (props, ref) {
  var _cx, _cx2;

  var file = props.file,
      showSize = props.showSize,
      error = props.error,
      multiple = props.multiple,
      size = props.size,
      onRemove = props.onRemove;
  var id = file.id,
      originalFile = file.originalFile,
      status = file.status,
      validationResult = file.validationResult;
  var name = originalFile.name,
      fileSize = originalFile.size;

  var _useState = useState(false),
      hovered = _useState[0],
      setHovered = _useState[1];

  var _useState2 = useState(false),
      focusedByTab = _useState2[0],
      setFocusedByTab = _useState2[1];

  var _useState3 = useState(null),
      truncatedFileName = _useState3[0],
      setTruncatedFileName = _useState3[1];

  var textHelperRef = useRef(null);
  var fileNameElementRef = useRef(null);

  var _useContext = useContext(FileUploaderControlContext),
      removeFile = _useContext.removeFile,
      setIsMinLengthReached = _useContext.setIsMinLengthReached,
      isMinLengthReached = _useContext.isMinLengthReached;

  var theme = useContext(ThemeContext);
  var formattedSize = useMemo(function () {
    return formatBytes(fileSize, 1);
  }, [fileSize]);
  useEffect(function () {
    if (setIsMinLengthReached) {
      var _truncatedName$length;

      var truncatedName = calcTruncatedName(textHelperRef, fileNameElementRef, name);
      setIsMinLengthReached(((_truncatedName$length = truncatedName == null ? void 0 : truncatedName.length) != null ? _truncatedName$length : 0) <= MIN_CHARS_LENGTH);
    }
  }, [name, isMinLengthReached]);
  useEffect(function () {
    var truncatedName = calcTruncatedName(textHelperRef, fileNameElementRef, name);
    setTruncatedFileName(truncatedName);
  });
  var removeUploadFile = useCallback(function () {
    removeFile(id);
    onRemove(id);
  }, [removeFile, id, onRemove]);
  var handleRemove = useCallback(function (event) {
    event.preventDefault();
    event.stopPropagation();
    removeUploadFile();
  }, [removeUploadFile]);
  var isValid = validationResult.isValid,
      message = validationResult.message;
  var isInvalid = error || !isValid;
  var sizeIconClass = useFileUploaderSize(size, {
    small: jsStyles.iconSmall(),
    medium: jsStyles.iconMedium(),
    large: jsStyles.iconLarge()
  });
  var renderTooltipContent = useCallback(function () {
    return !isValid && !error && message ? message : null;
  }, [isValid, error, message]);
  var sizeContentClass = useFileUploaderSize(size, {
    small: jsStyles.contentSmall(theme),
    medium: jsStyles.contentMedium(theme),
    large: jsStyles.contentLarge(theme)
  });
  var contentClassNames = cx(jsStyles.content(), (_cx = {}, _cx[sizeContentClass] = true, _cx[jsStyles.error(theme)] = isInvalid, _cx));
  var handleMouseEnter = useCallback(function () {
    setHovered(true);
  }, []);
  var handleMouseLeave = useCallback(function () {
    setHovered(false);
  }, []);
  var handleFocus = useCallback(function () {
    // focus event fires before keyDown eventlistener
    // so we should check tabPressed in async way
    globalObject.requestAnimationFrame == null ? void 0 : globalObject.requestAnimationFrame(function () {
      if (keyListener.isTabPressed) {
        setFocusedByTab(true);
      }
    });
  }, []);
  var handleBlur = useCallback(function () {
    setFocusedByTab(false);
  }, []);
  var handleIconKeyDown = useCallback(function (e) {
    if (isKeyEnter(e)) {
      removeUploadFile();
    }
  }, [removeUploadFile]);
  var iconClassNames = cx(jsStyles.icon(theme), (_cx2 = {}, _cx2[jsStyles.focusedIcon(theme)] = focusedByTab, _cx2[sizeIconClass] = true, _cx2[jsStyles.iconMultiple()] = multiple, _cx2));
  var isTruncated = truncatedFileName !== name;
  return /*#__PURE__*/React.createElement("div", {
    "data-tid": FileUploaderFileDataTids.file,
    className: jsStyles.root(),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    ref: ref
  }, /*#__PURE__*/React.createElement(Tooltip, {
    "data-tid": FileUploaderFileDataTids.fileTooltip,
    pos: "right middle",
    render: renderTooltipContent
  }, /*#__PURE__*/React.createElement("div", {
    className: contentClassNames
  }, /*#__PURE__*/React.createElement(TextWidthHelper, {
    ref: textHelperRef,
    text: name
  }), /*#__PURE__*/React.createElement(Hint, {
    maxWidth: '100%',
    text: isTruncated ? name : null
  }, /*#__PURE__*/React.createElement("span", {
    "data-tid": FileUploaderFileDataTids.fileName,
    ref: fileNameElementRef,
    className: jsStyles.name()
  }, truncatedFileName)), !!showSize && formattedSize && /*#__PURE__*/React.createElement("span", {
    "data-tid": FileUploaderFileDataTids.fileSize,
    className: jsStyles.size()
  }, formattedSize), /*#__PURE__*/React.createElement("div", {
    className: iconClassNames,
    "data-tid": FileUploaderFileDataTids.fileIcon,
    tabIndex: 0,
    onClick: handleRemove,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleIconKeyDown
  }, /*#__PURE__*/React.createElement(FileUploaderFileStatusIcon, {
    status: status,
    hovered: hovered,
    focusedByTab: focusedByTab,
    isInvalid: isInvalid,
    size: size
  })))));
});